import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/core/auth/auth';
import { useAuditLogs } from '@/core/hooks/use-audit-logs';

export const LEGAL_DOC_VERSIONS = {
  terms: '2026-02-26',
  privacy: '2026-02-26',
  hipaa: '2026-02-26',
} as const;

function storageKey(userId: string) {
  return `rl:legalAccepted:${userId}`;
}

export function hasAcceptedLegal(userId?: string | null): boolean {
  if (!userId) return false;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { versions?: typeof LEGAL_DOC_VERSIONS };
    return (
      parsed?.versions?.terms === LEGAL_DOC_VERSIONS.terms &&
      parsed?.versions?.privacy === LEGAL_DOC_VERSIONS.privacy &&
      parsed?.versions?.hipaa === LEGAL_DOC_VERSIONS.hipaa
    );
  } catch {
    return false;
  }
}

interface LegalGateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccepted?: () => void;
}

export function LegalGateModal({ open, onOpenChange, onAccepted }: LegalGateModalProps) {
  const { user } = useAuth();
  const { logAction } = useAuditLogs();

  const alreadyAccepted = useMemo(() => hasAcceptedLegal(user?.id), [user?.id]);

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [ackHipaa, setAckHipaa] = useState(false);
  const [ackNoIdentifiers, setAckNoIdentifiers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAgreeTerms(false);
      setAgreePrivacy(false);
      setAckHipaa(false);
      setAckNoIdentifiers(false);
      setSubmitting(false);
    }
  }, [open]);

  const canSubmit = user && (alreadyAccepted || (agreeTerms && agreePrivacy && ackHipaa && ackNoIdentifiers));

  const handleAccept = async () => {
    if (!user) return;
    if (alreadyAccepted) {
      onOpenChange(false);
      onAccepted?.();
      return;
    }

    setSubmitting(true);
    try {
      // Persist locally so gating is instant and resilient.
      localStorage.setItem(
        storageKey(user.id),
        JSON.stringify({
          acceptedAt: new Date().toISOString(),
          versions: LEGAL_DOC_VERSIONS,
          acknowledgements: {
            agreeTerms: true,
            agreePrivacy: true,
            ackHipaa: true,
            ackNoIdentifiers: true,
          },
        })
      );

      // Also log to audit trail if permitted by RLS.
      await logAction('legal_accepted', {
        ...LEGAL_DOC_VERSIONS,
        ackNoIdentifiers: true,
        surface: 'portal',
      });

      onOpenChange(false);
      onAccepted?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">Before you continue</DialogTitle>
          <DialogDescription className="text-base">
            Please review and accept our policies before downloading LiveSpeak Desktop or using clinical features.
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">
              Please sign in to continue.
            </p>
            <div className="mt-3 flex gap-2">
              <Button asChild>
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/signup">Create account</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox checked={agreeTerms} onCheckedChange={(v) => setAgreeTerms(Boolean(v))} />
              <div className="text-sm">
                I agree to the{' '}
                <Link to="/terms" className="text-primary hover:underline" target="_blank" rel="noreferrer">
                  Terms of Service
                </Link>
                .
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox checked={agreePrivacy} onCheckedChange={(v) => setAgreePrivacy(Boolean(v))} />
              <div className="text-sm">
                I agree to the{' '}
                <Link to="/privacy" className="text-primary hover:underline" target="_blank" rel="noreferrer">
                  Privacy Policy
                </Link>
                .
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox checked={ackHipaa} onCheckedChange={(v) => setAckHipaa(Boolean(v))} />
              <div className="text-sm">
                I have read the{' '}
                <Link to="/hipaa" className="text-primary hover:underline" target="_blank" rel="noreferrer">
                  Healthcare Compliance (HIPAA)
                </Link>{' '}
                page.
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox checked={ackNoIdentifiers} onCheckedChange={(v) => setAckNoIdentifiers(Boolean(v))} />
              <div className="text-sm">
                I understand I must not enter patient identifiers (name/MRN/DOB/accession) unless and until RadiologyLive.ai
                explicitly enables a HIPAA/BAA-backed workflow.
              </div>
            </div>

            <div className="pt-2 flex flex-col gap-2">
              <Button disabled={!canSubmit || submitting} onClick={handleAccept}>
                {alreadyAccepted ? 'Continue' : 'Accept & Continue'}
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Not now
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Versions — Terms: {LEGAL_DOC_VERSIONS.terms} · Privacy: {LEGAL_DOC_VERSIONS.privacy} · HIPAA: {LEGAL_DOC_VERSIONS.hipaa}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
